import React, { useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { notApprovableRankIds } from '@modules/advancement/constants';
import {
  GroupedPendingItem,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import { searchOnItems } from '@modules/advancement/purchaseOrder/utils';
import { ApproveAdvancementsButton } from '@modules/progress/approveAdvancements';
import { programTypeName } from '@modules/shared';
import { PaginationTotal, Table } from '@shared/components';

import styles from './PendingItemsTable.less';
import { groupedColumns } from './PendingRequirementsColumns';

type SelectedItemKey = string | number;

interface PendingRequirementsTableNotApprovable {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
  searchQuery: string;
  notApprovableItemsWithChildren: GroupedPendingItem[];
  isLoading: boolean;
}

const PendingRequirementsTableNotApprovable: React.FC<
  PendingRequirementsTableNotApprovable
> = props => {
  const {
    searchQuery,
    notApprovableItemsWithChildren: dataSource,
    isLoading,
    onGoToYouthProfile,
    onGoToAdultProfile,
  } = props;

  const [selectedRowKeys, setSelectedRowKeys] = useState<SelectedItemKey[]>([]);
  const searchedItemsNotApprovable = searchOnItems(searchQuery, dataSource);
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const selectedAdvancements = useMemo(() => {
    const setKeys = new Set(selectedRowKeys);

    return dataSource
      .flatMap(({ children }) => children)
      .filter(item => setKeys.has(item.key))
      .filter(Boolean);
  }, [dataSource, selectedRowKeys]);

  const renderTitle = () => (
    <React.Fragment>
      <ApproveAdvancementsButton
        className={styles.approveAdvancements}
        disabled={!areAnyAdvancementsSelected}
        personsAdvancements={selectedAdvancements}
        advancementStatus={null}
      />
    </React.Fragment>
  );

  const isNotApprovable = (record: GroupedPendingItem | PendingItem) => {
    if (
      (record as PendingItem).program !== undefined &&
      ((record as PendingItem).program !== programTypeName.CUB_SCOUT ||
        notApprovableRankIds.includes((record as PendingItem).id))
    ) {
      return true;
    }

    if (
      (record as GroupedPendingItem).totalAmount !== undefined &&
      (record as GroupedPendingItem).totalAmount === 0
    ) {
      return true;
    }

    return false;
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record: GroupedPendingItem | PendingItem) => ({
      disabled: isNotApprovable(record),
      style: isNotApprovable(record) ? { display: 'none' } : {},
    }),
    onSelect(record: GroupedPendingItem | PendingItem, selected: boolean) {
      const isGroup = 'groupId' in record;
      if (selected) {
        if (isGroup) {
          const selected = record.children.map(item => item.key);
          setSelectedRowKeys([...selectedRowKeys, ...selected, record.key]);
        } else {
          setSelectedRowKeys([...selectedRowKeys, record.key]);
        }
      } else if (isGroup) {
        const selectedItems = new Set<string | number>(selectedRowKeys);
        selectedItems.delete(record.key);
        record.children.forEach(item => {
          selectedItems.delete(item.key);
        });
        setSelectedRowKeys(Array.from(selectedItems));
      } else {
        const currentSelectedKeys = new Set<string | number>(selectedRowKeys);
        currentSelectedKeys.delete(record.key);
        setSelectedRowKeys(Array.from(currentSelectedKeys));
      }
    },
    columnTitle: <></>,
  };

  return (
    <div className={styles.notApprovableContainer}>
      <p className={styles.disclaimer}>
        <FormattedMessage id="advancement.PendingItems.nonApprovable.disclaimer" />
      </p>
      <Table
        dataSource={searchedItemsNotApprovable}
        loading={isLoading}
        className={styles.table}
        title={renderTitle}
        columns={groupedColumns({
          onGoToYouthProfile,
          onGoToAdultProfile,
        })}
        rowSelection={rowSelection}
        pagination={{
          showTotal: () => {
            const totalSelected = selectedRowKeys.filter(
              (item: string) => !item?.includes('group'),
            ).length;
            const totalNoGroup = dataSource.flatMap(
              item => item.children,
            )?.length;

            return (
              <PaginationTotal total={totalNoGroup} selected={totalSelected} />
            );
          },
        }}
        rowClassName={(row: GroupedPendingItem) => {
          if (isEmpty(row?.children)) return styles.rowItem;
          else return styles.rowItemGroup;
        }}
        size="small"
      />
    </div>
  );
};

export default PendingRequirementsTableNotApprovable;
